import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Particles from "./Particles";

export default function Hero({ config }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end end"],
  });

  const manifestoY = useTransform(scrollYProgress, [0, 0.35], ["0%", "-30%"]);
  const manifestoO = useTransform(scrollYProgress, [0.18, 0.34], [1, 0]);
  const namesO = useTransform(scrollYProgress, [0.4, 0.58], [0, 1]);
  const namesY = useTransform(scrollYProgress, [0.4, 0.58], [60, 0]);
  const dateO = useTransform(scrollYProgress, [0.66, 0.82], [0, 1]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  // Photo emerges from behind, blending gradually while the couple names sit on top of it.
  const photoO = useTransform(scrollYProgress, [0.42, 0.85], [0, 1]);
  const photoScale = useTransform(scrollYProgress, [0.42, 1], [1.15, 1]);
  const overlayO = useTransform(scrollYProgress, [0.42, 0.85], [0, 0.55]);

  const { partnerOne, partnerTwo } = config.couple;

  return (
    <section ref={ref} className="relative h-[280vh] bg-deep-black" data-testid="hero-section">
      <div className="sticky top-0 flex h-screen items-center justify-center overflow-hidden">
        <motion.div style={{ scale }} className="absolute inset-0">
          <Particles color="187, 166, 128" count={70} />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_40%,rgba(187,166,128,0.12),transparent_60%)]" />
        </motion.div>

        {/* Photo revealed progressively as user scrolls, staying behind the names */}
        <motion.div
          aria-hidden="true"
          style={{ opacity: photoO, scale: photoScale }}
          className="absolute inset-0 z-[5]"
        >
          <img
            src="https://customer-assets-gfyr7b9c.emergentagent.net/job_cinematic-vows-29/artifacts/joo7x1w0__DSC4222_2.webp"
            alt=""
            className="h-full w-full object-cover object-center"
            loading="eager"
            fetchpriority="high"
          />
          <motion.div
            style={{ opacity: overlayO }}
            className="absolute inset-0 bg-[linear-gradient(105deg,rgba(30,33,22,0.78)_0%,rgba(30,33,22,0.45)_45%,rgba(30,33,22,0.15)_100%)]"
          />
          <motion.div
            style={{ opacity: overlayO }}
            className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(30,33,22,0.35)_0%,transparent_25%,transparent_65%,rgba(30,33,22,0.55)_100%)]"
          />
        </motion.div>

        {/* Manifesto */}
        <motion.div
          style={{ y: manifestoY, opacity: manifestoO }}
          className="relative z-10 px-6 text-center"
        >
          {config.manifesto.map((line, i) => (
            <span key={i} className="line-mask">
              <motion.span
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1.4, ease: [0.76, 0, 0.24, 1], delay: 0.3 + i * 0.18 }}
                className="block font-serif text-4xl font-light leading-[1.05] tracking-tight text-ivory md:text-7xl lg:text-[6vw]"
              >
                {line}
              </motion.span>
            </span>
          ))}
        </motion.div>

        {/* Couple reveal — editorial cover composition */}
        <motion.div
          style={{ opacity: namesO, y: namesY }}
          className="absolute inset-0 z-10 flex flex-col justify-between px-6 py-24 sm:px-10 sm:py-28 md:px-16 md:py-32 lg:px-24"
        >
          <div />

          <div className="max-w-5xl">
            <p className="mb-6 flex items-center gap-4 text-[10px] uppercase tracking-[0.5em] text-gold sm:text-xs md:mb-8">
              <span className="h-px w-10 bg-gold" />
              La boda de
            </p>
            <h1 className="font-serif font-light italic leading-[0.92] tracking-tight text-ivory text-[clamp(3.25rem,8.5vw,9rem)]">
              {partnerOne}
            </h1>
            <span className="my-1 block font-serif font-light not-italic text-gold text-[clamp(1.75rem,3.5vw,3.5rem)]">
              &amp;
            </span>
            <h1 className="font-serif font-light italic leading-[0.92] tracking-tight text-ivory text-[clamp(3.25rem,8.5vw,9rem)]">
              {partnerTwo}
            </h1>
          </div>

          <motion.div style={{ opacity: dateO }} className="flex flex-col">
            <span className="mb-4 block h-px w-16 bg-ivory/40" />
            <p className="text-[10px] uppercase tracking-[0.5em] text-ivory/70 sm:text-xs">
              {config.announcement}
            </p>
            <p className="mt-2 font-serif text-2xl italic text-ivory md:text-3xl">
              {config.weddingDateLabel}
            </p>
          </motion.div>
        </motion.div>

        <motion.div
          style={{ opacity: useTransform(scrollYProgress, [0, 0.1], [1, 0]) }}
          className="absolute bottom-10 left-1/2 z-10 -translate-x-1/2 text-center"
        >
          <p className="text-[10px] uppercase tracking-[0.4em] text-ivory/50">Desliza para comenzar</p>
          <div className="mx-auto mt-3 h-10 w-px bg-gradient-to-b from-gold to-transparent" />
        </motion.div>
      </div>
    </section>
  );
}
